/**
 * Disputes Dashboard Metrics Hook
 *
 * Provides role-based filtered dispute metrics for the Disputes Dashboard.
 * Automatically filters disputes based on the authenticated user's role and permissions.
 *
 * Role-Based Filtering:
 * - credit-controller: See only their assigned disputes
 * - manager/team-leader: See all team disputes (can filter to individuals)
 * - fp-a/finance-director: See all disputes across all teams
 */

import { useMemo } from 'react';
import { CONTROLLERS } from '@/mockData/controllers';
import { DISPUTES } from '@/mockData/disputes';
import type { UserRole } from '@/types/roles';
import { useAuth } from './use-auth';

// Dispute type definition
export interface Dispute {
  id: string;
  customerId: string;
  assignedTo: string;
  amount: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'requiring-response' | 'in-progress' | 'resolved';
  reason: string;
  openedDate: string;
  daysOpen: number;
  company?: string;
}

// Metrics returned by the hook
export interface DisputesMetrics {
  // Top-level metrics
  activeCount: number;
  totalAmount: number;
  avgResolutionTime: number;
  resolutionRate: number;

  // Disputes by priority
  byPriority: {
    critical: { count: number; amount: number };
    high: { count: number; amount: number };
    medium: { count: number; amount: number };
    low: { count: number; amount: number };
  };

  // Dispute reasons analysis
  byReason: Array<{
    reason: string;
    count: number;
    percentage: number;
  }>;

  // Recent disputes list
  recentDisputes: Dispute[];

  // All filtered disputes
  allDisputes: Dispute[];

  // Metadata
  isFiltered: boolean;
  filterType: 'individual' | 'team' | 'all';
  userName: string;
}

/**
 * Determines which disputes a user can access based on their role
 */
function getAccessibleDisputes(userId: string, userRole: UserRole): Dispute[] {
  console.log('[useDisputesMetrics] Filtering disputes for:', { userId, userRole });

  // Managers and above can see all disputes
  const managerRoles: UserRole[] = ['manager', 'team-leader', 'fp-a', 'finance-director'];

  if (managerRoles.includes(userRole)) {
    console.log('[useDisputesMetrics] Manager role - returning all disputes:', DISPUTES.length);
    return DISPUTES;
  }

  // Credit controllers see only their assigned disputes
  const userDisputes = DISPUTES.filter((d) => d.assignedTo === userId);
  console.log('[useDisputesMetrics] Credit controller - returning assigned disputes:', userDisputes.length);

  return userDisputes;
}

/**
 * Calculates comprehensive dispute metrics from filtered disputes
 */
function calculateMetrics(
  disputes: Dispute[],
  isFiltered: boolean,
  filterType: 'individual' | 'team' | 'all',
  userName: string,
): DisputesMetrics {
  // Active disputes (requiring response)
  const activeDisputes = disputes.filter((d) => d.status === 'requiring-response');

  // Calculate by priority
  const byPriority = {
    critical: {
      count: activeDisputes.filter((d) => d.priority === 'critical').length,
      amount: activeDisputes.filter((d) => d.priority === 'critical').reduce((sum, d) => sum + d.amount, 0),
    },
    high: {
      count: activeDisputes.filter((d) => d.priority === 'high').length,
      amount: activeDisputes.filter((d) => d.priority === 'high').reduce((sum, d) => sum + d.amount, 0),
    },
    medium: {
      count: activeDisputes.filter((d) => d.priority === 'medium').length,
      amount: activeDisputes.filter((d) => d.priority === 'medium').reduce((sum, d) => sum + d.amount, 0),
    },
    low: {
      count: activeDisputes.filter((d) => d.priority === 'low').length,
      amount: activeDisputes.filter((d) => d.priority === 'low').reduce((sum, d) => sum + d.amount, 0),
    },
  };

  // Calculate by reason
  const reasonCounts = activeDisputes.reduce(
    (acc, d) => {
      acc[d.reason] = (acc[d.reason] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const byReason = Object.entries(reasonCounts)
    .map(([reason, count]) => ({
      reason,
      count,
      percentage: Math.round((count / activeDisputes.length) * 100),
    }))
    .sort((a, b) => b.count - a.count);

  // Get recent disputes (last 5, sorted by days open descending)
  const recentDisputes = [...activeDisputes]
    .sort((a, b) => b.daysOpen - a.daysOpen)
    .slice(0, 5)
    .map((d) => {
      // Add company name from controller assignment
      const controller = CONTROLLERS.find((c) => c.id === d.assignedTo);
      return {
        ...d,
        company: `${controller?.name || 'Unknown'}'s Customer ${d.customerId}`,
      };
    });

  // Calculate total amount
  const totalAmount = activeDisputes.reduce((sum, d) => sum + d.amount, 0);

  return {
    activeCount: activeDisputes.length,
    totalAmount,
    avgResolutionTime: 14.3, // Mock - would calculate from resolved disputes
    resolutionRate: 87.5, // Mock - would calculate from historical data
    byPriority,
    byReason,
    recentDisputes,
    allDisputes: activeDisputes,
    isFiltered,
    filterType,
    userName,
  };
}

/**
 * Hook to get role-based filtered dispute metrics
 *
 * @param manualFilter - Optional filter override (for managers to view individual performance)
 * @returns Comprehensive dispute metrics filtered by role and permissions
 */
export function useDisputesMetrics(manualFilter?: string): DisputesMetrics {
  const { user } = useAuth();

  const metrics = useMemo(() => {
    console.log('[useDisputesMetrics] Calculating metrics for user:', user.name, 'role:', user.role);

    // Determine effective filter
    let effectiveFilter = user.id;
    let filterType: 'individual' | 'team' | 'all' = 'individual';

    // Managers can see all disputes or filter to specific users
    const managerRoles: UserRole[] = ['manager', 'team-leader', 'fp-a', 'finance-director'];
    if (managerRoles.includes(user.role)) {
      if (manualFilter && manualFilter !== 'everyone') {
        // Manager viewing specific user's disputes
        effectiveFilter = manualFilter;
        filterType = 'individual';
      } else {
        // Manager viewing all disputes
        effectiveFilter = 'everyone';
        filterType = 'team';
      }
    }

    // Get accessible disputes based on role
    let disputes: Dispute[];

    if (effectiveFilter === 'everyone') {
      // Get all disputes (manager view)
      disputes = getAccessibleDisputes(user.id, user.role);
    } else {
      // Get specific user's disputes
      disputes = DISPUTES.filter((d) => d.assignedTo === effectiveFilter);
    }

    // Determine if data is filtered
    const isFiltered = effectiveFilter !== 'everyone';
    const userName = isFiltered ? CONTROLLERS.find((c) => c.id === effectiveFilter)?.name || user.name : 'Team';

    console.log('[useDisputesMetrics] Filtered disputes:', {
      effectiveFilter,
      filterType,
      disputeCount: disputes.length,
      userName,
    });

    return calculateMetrics(disputes, isFiltered, filterType, userName);
  }, [user.id, user.name, user.role, manualFilter]);

  return metrics;
}

/**
 * Hook to check if user has access to view all disputes
 */
export function useCanViewAllDisputes(): boolean {
  const { user } = useAuth();
  const managerRoles: UserRole[] = ['manager', 'team-leader', 'fp-a', 'finance-director'];
  return managerRoles.includes(user.role);
}

/**
 * Hook to get list of users whose disputes the current user can view
 */
export function useAccessibleUsers(): Array<{ id: string; name: string }> {
  const { user } = useAuth();
  const canViewAll = useCanViewAllDisputes();

  if (canViewAll) {
    // Managers can view all controllers
    return CONTROLLERS.map((c) => ({ id: c.id, name: c.name }));
  }

  // Credit controllers can only view their own
  return [{ id: user.id, name: user.name }];
}
